import React, { useState, useEffect } from "react";
import { useT } from '../i18n';
import { useLocation, useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import lbDialog from '../components/UI/Modals/lbDialog';
import { useAppKitAccount } from "@reown/appkit/react";
import StakingABI from "../abi/staking_abi.json";
import IERC20ABI from "../abi/ERC20_ABI.json";
import {
  stakingContract,
  usdtContractAddress,
  cranniAddress,
  lpToken,
  provider,
  wsprovider,
  bckpProvider,
  CRANNILuckBlocksAddress,
  maticLuckBlocksAddress,
  chainId,
} from "../.config";
import { TrendingUp, Coins, Gift, Droplet, AlertTriangle, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import CRANNILuckBlocks from "../abi/CRANNILuckblocks.json";
import MaticLuckBlocks from "../abi/MATICLuckBlocks.json";
import lpTokenABI from "../abi/LPtoken_abi.json";
import MintBurn from "../components/MintBurn/MintBurn";
import { useWriteContract, useReadContract, useReadContracts } from "wagmi";

// BSC testnet: the staking LP is a test token anyone can mint (no DEX pair to add liquidity to)
const TESTNET_CHAIN = 97;
const TEST_LP_AMOUNT = 100n * 10n ** 18n;
const TEST_MINT_ABI = [
  {
    type: "function",
    name: "mint",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [],
  },
];

const StatCard = ({ title, value, icon: Icon, className = "", onClaim, address, rewards }) => {
  const t = useT();

  return (
  <div className={`lb-tile lb-hoverable flex items-start gap-4 p-5 ${className}`}>
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5">
      {Icon}
    </div>
    <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="lb-label">{title}</p>
        <div className="notranslate lb-num mt-1.5 break-words text-lg font-bold text-white">
          {value}
        </div>
      </div>
      {title === t('token.pendingRewards') && address && rewards > 0 && (
        <button onClick={onClaim} className="lb-btn lb-btn-primary lb-btn-sm shrink-0">
          {t('token.claim')}
        </button>
      )}
    </div>
  </div>
  );
};

const DebtWarning = ({ userUnpaidRewards }) => {
  const t = useT();
  if (userUnpaidRewards <= 0) return null;
  
  return (
    <div className="mb-6 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 to-rose-500/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400">
          <AlertTriangle size={15} className="text-black" />
        </div>
        <div>
          <h4 className="font-bold text-amber-300">{t('token.unpaidNotice')}</h4>
          <p className="mt-1 text-sm text-amber-100/80">
            {t('token.youHave')} <span className="notranslate font-bold">{userUnpaidRewards} USDT</span> in unpaid
            rewards. These are paid automatically once the contract receives new reward tokens.
          </p>
        </div>
      </div>
    </div>
  );
};

// Splits a translated title around its {{accent}} word so that word keeps the gradient,
// whatever position the language puts it in.
const ACCENT = '\u0000';
const AccentTitle = ({ text, accent }) => {
  const [before, after = ''] = text.split(ACCENT);
  return (
    <>
      {before}
      <span className="lb-gradient-text">{accent}</span>
      {after}
    </>
  );
};

const Token = () => {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const [pendingRewards, setPendingRewards] = useState(0);
  const [inputValue, setInputValue] = useState("0");
  const [amount, setAmount] = useState("0");
  const [totalRewardBalance, setTotalRewardBalance] = useState(0);
  const [pendingRewardTotal, setPendingRewardTotal] = useState(0);
  const [userStakedAmount, setUserStakedAmount] = useState(0);
  const [rewardPerBlock, setRewardPerBlock] = useState(0);
  const [rewardBalance, setRewardBalance] = useState(0);
  const [totalStaked, setTotalStaked] = useState(0);
  const [pendPenalization, setPendPenalization] = useState(0);
  const [lpTokenBalance, setLpTokenBalance] = useState(0);
  const [apr, setApr] = useState(0);
  const [isDepositMode, setIsDepositMode] = useState(true);
  const [cranniPrice, setCranniPrice] = useState(0);
  const [cranniMarketCap, setCranniMarketCap] = useState(0);
  const [cranniTotalSupply, setCranniTotalSupply] = useState(0);
  const [polPrice, setPolPrice] = useState(0);
  const [lpPrice, setLPPrice] = useState(0);
  
  // New debt system state variables
  const [totalUnpaidRewards, setTotalUnpaidRewards] = useState(0);
  const [userUnpaidRewards, setUserUnpaidRewards] = useState(0);
  const [availableRewards, setAvailableRewards] = useState(0);
  
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { writeContractAsync } = useWriteContract();

  const getInitialSection = () => {
    const section = searchParams.get('section');
    if (section && ['token', 'staking', 'cynths'].includes(section)) {
      return section === 'staking' ? 'stakingDashboard' : section;
    }

    if (location.state?.activeSection) {
      return location.state.activeSection;
    }

    return "token";
  };

  const [activeSection, setActiveSection] = useState(getInitialSection());

  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['token', 'staking', 'cynths'].includes(section)) {
      const mappedSection = section === 'staking' ? 'stakingDashboard' : section;
      setActiveSection(mappedSection);
    }
  }, [searchParams]);

  // Fetch prices using useReadContract
  const { data: cranniPriceData } = useReadContract({
    address: CRANNILuckBlocksAddress,
    abi: CRANNILuckBlocks.abi,
    functionName: "getLatestPrice",
  });

  const { data: maticPriceData } = useReadContract({
    address: maticLuckBlocksAddress,
    abi: MaticLuckBlocks.abi,
    functionName: "getLatestPrice",
  });

  // Process prices
  useEffect(() => {
    if (cranniPriceData && maticPriceData) {
      const cranniCoinPrice = parseFloat(ethers.utils.formatUnits(cranniPriceData, 6));
      const maticCoinPrice = parseFloat(ethers.utils.formatUnits(maticPriceData, 18));
      setCranniPrice(cranniCoinPrice.toFixed(3));
      setPolPrice(maticCoinPrice.toFixed(2));
      const mkcap = (cranniCoinPrice * 8888888).toFixed(2);
      setCranniMarketCap(Number(mkcap));
      setCranniTotalSupply(8888888);
    }
  }, [cranniPriceData, maticPriceData]);

  // Fetch LP Token Balance
  const { data: lpTokenBalanceData, refetch: refetchLpBalance } = useReadContract({
    address: lpToken,
    abi: IERC20ABI.abi,
    functionName: "balanceOf",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  useEffect(() => {
    if (lpTokenBalanceData) {
      const formattedBalance = ethers.utils.formatUnits(lpTokenBalanceData, 18);
      setLpTokenBalance(parseFloat(formattedBalance).toFixed(18));
    }
  }, [lpTokenBalanceData]);

  // Fetch contract data
  const { data: contractData } = useReadContracts({
    contracts: [
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "totalRewardBalance",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "rewardPerBlock",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "rewardBalance",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "pendingRewardTotal",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      {
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "totalStaked",
        watch: true,
        enabled: !!address && isConnected,
        pollingInterval: 15000,
      },
      ...(address
        ? [
          {
            address: stakingContract,
            abi: StakingABI.abi,
            functionName: "pendingPenalization",
            args: [address],
            watch: true,
            enabled: !!address && isConnected,
            pollingInterval: 15000,
          },
        ]
        : []),
    ],
  });

  useEffect(() => {
    if (contractData && isConnected && address) {
      const [totalReward, rewardBlock, rewardBal, pendingRewardTotal, totalStakedAmt, penalizationTime] =
        contractData.map((d) => d.result);

      setTotalRewardBalance(Number(ethers.utils.formatUnits(totalReward || 0, 6)).toFixed(3));
      setRewardPerBlock(ethers.utils.formatUnits(rewardBlock || 0, 6));
      setRewardBalance(Number(ethers.utils.formatUnits(rewardBal || 0, 6)).toFixed(3));
      setPendingRewardTotal(ethers.utils.formatUnits(pendingRewardTotal || 0, 6));
      setTotalStaked(ethers.utils.formatUnits(totalStakedAmt || 0, 18));

      if (address && penalizationTime) {
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const penaltyEndTimestamp = Number(penalizationTime);
        const timeDifference = penaltyEndTimestamp - currentTimestamp;
        processPenalization(timeDifference);
      }
    } else if (!isConnected || !address) {
      setRewardBalance(0);
      setPendPenalization("0");
    }
  }, [contractData, address, isConnected]);

  // Fetch user staked amount
  const { data: userInfo } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "userInfo",
    args: [0, address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  useEffect(() => {
    if (userInfo) {
      const formattedAmount = ethers.utils.formatUnits(userInfo[1], 18);
      setUserStakedAmount(formattedAmount);
    }
  }, [userInfo]);

  // Fetch pending rewards with new debt system format
  const { data: rewards } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "pendingReward",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
    pollingInterval: 15000,
  });

  // pendingReward already includes rewards left unpaid by earlier claims (unpaidRewards); a claim pays
  // what the contract's free reward balance covers and keeps the rest as unpaid
  useEffect(() => {
    if (rewards === undefined) return;
    const pending = Number(ethers.utils.formatUnits(rewards, 6));
    setPendingRewards(pending.toFixed(3));
    setAvailableRewards(Math.min(pending, Number(rewardBalance) || 0).toFixed(3));
  }, [rewards, rewardBalance]);

  // Rewards accrued to stakers and not paid out yet
  const { data: totalDebt } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "totalCommittedRewards",
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: userDebt } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "unpaidRewards",
    args: [address],
    watch: true,
    enabled: !!address && isConnected,
  });

  useEffect(() => {
    if (totalDebt !== undefined) {
      setTotalUnpaidRewards(Number(ethers.utils.formatUnits(totalDebt, 6)).toFixed(3));
    }
  }, [totalDebt]);

  useEffect(() => {
    if (userDebt !== undefined) {
      setUserUnpaidRewards(Number(ethers.utils.formatUnits(userDebt, 6)).toFixed(3));
    }
  }, [userDebt]);

  // Fetch LP Token data for APR calculation
  const { data: token0 } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "token0",
  });

  const { data: token1 } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "token1",
  });

  const { data: reserves } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "getReserves",
  });

  const { data: token0Decimals } = useReadContract({
    address: token0,
    abi: IERC20ABI.abi,
    functionName: "decimals",
    enabled: !!token0,
  });

  const { data: token1Decimals } = useReadContract({
    address: token1,
    abi: IERC20ABI.abi,
    functionName: "decimals",
    enabled: !!token1,
  });

  const { data: totalSupply } = useReadContract({
    address: lpToken,
    abi: lpTokenABI.abi,
    functionName: "totalSupply",
  });

  // Calculate APR
  useEffect(() => {
    if (token0 && token1 && reserves && token0Decimals && token1Decimals && totalSupply && polPrice && cranniPrice) {
      try {
        const reserve0 = ethers.utils.formatUnits(reserves[0], token0Decimals);
        const reserve1 = ethers.utils.formatUnits(reserves[1], token1Decimals);
        const token0Price = parseFloat(polPrice);
        const token1Price = parseFloat(cranniPrice);
        const liquidityValue = parseFloat(reserve0) * token0Price + parseFloat(reserve1) * token1Price;
        const formattedTotalSupply = ethers.utils.formatUnits(totalSupply, 18);
        const lpTokenPrice = liquidityValue / parseFloat(formattedTotalSupply);
        setLPPrice(lpTokenPrice.toFixed(2));

        const BLOCKS_PER_YEAR = (365 * 24 * 60 * 60) / (chainId === 97 ? 3 : 2);
        const rewardTokenPrice = 1; // Assuming USDT price is 1
        const yearlyRewards = parseFloat(rewardPerBlock) * BLOCKS_PER_YEAR * rewardTokenPrice;
        const totalStakedValue = parseFloat(totalStaked) * lpTokenPrice;
        if (totalStakedValue > 0) {
          const calculatedApr = (yearlyRewards / totalStakedValue) * 100;
          setApr(calculatedApr.toFixed(2));
        } else {
          setApr(0);
        }
      } catch (err) {
        console.error("Error calculating APR:", err);
        setApr(0);
      }
    }
  }, [token0, token1, reserves, token0Decimals, token1Decimals, totalSupply, polPrice, cranniPrice, rewardPerBlock, totalStaked]);

  // Existing contract read hooks
  const { data: allowanceAutoBuy } = useReadContract({
    address: cranniAddress,
    abi: IERC20ABI.abi,
    functionName: "allowance",
    args: [address, cranniAddress],
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: lowerAmount } = useReadContract({
    address: stakingContract,
    abi: StakingABI.abi,
    functionName: "lowerAmount",
    watch: true,
    enabled: !!address && isConnected,
  });

  const { data: allowanceDeposit } = useReadContract({
    address: lpToken,
    abi: IERC20ABI.abi,
    functionName: "allowance",
    args: [address, stakingContract],
    watch: true,
    enabled: !!address && isConnected,
  });

  useEffect(() => {
    if (!isConnected || !address) {
      setPendingRewards(0);
      setUserStakedAmount(0);
      setLpTokenBalance(0);
      setUserUnpaidRewards(0);
      setAvailableRewards(0);
    }
  }, [isConnected, address]);

  const processPenalization = (timeDifference) => {
    if (timeDifference > 0) {
      if (timeDifference >= 86400) {
        const days = Math.ceil(timeDifference / 86400);
        setPendPenalization(`${days} day${days > 1 ? "s" : ""}`);
      } else {
        const hours = Math.floor(timeDifference / 3600);
        const minutes = Math.floor((timeDifference % 3600) / 60);
        const seconds = timeDifference % 60;
        setPendPenalization(`${hours}h ${minutes}m ${seconds}s`);
      }
    } else {
      setPendPenalization("0");
    }
  };

  const setMaxAmount = () => {
    if (isDepositMode) {
      setAmount(lpTokenBalance);
    } else {
      setAmount(userStakedAmount);
    }
  };

  const approveForAutoBuy = async () => {
    if (!address) {
      lbDialog.fire({
        icon: "warning",
        title: t('token.notConnected'),
        text: t('token.connectToApprove'),
        timer: 2000,
        timerProgressBar: true,
      });
      return;
    }

    if (allowanceAutoBuy > 0) {
      lbDialog.fire({
        icon: "success",
        title: t('token.alreadyApproved'),
        text: t('token.alreadyApprovedAuto'),
        timer: 2000,
        timerProgressBar: true,
      });
      return;
    }

    try {
      const approveHash = await writeContractAsync({
        address: cranniAddress,
        abi: IERC20ABI.abi,
        functionName: "approve",
        args: [cranniAddress, ethers.constants.MaxUint256],
      });

      const approveReceipt = await provider.waitForTransaction(approveHash);
      if (!approveReceipt.status) throw new Error("Approval transaction failed.");

      lbDialog.fire({
        icon: "success",
        title: t('token.approvalSuccess'),
        text: t('token.approvedAuto'),
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error("Error during approval:", err);
      lbDialog.fire(t('tx.error'), t('token.approvalFailed'), "error");
    }
  };

  // BSC testnet only: the CRANNI-POL LP is a test token with an open mint, like the faucet's USDT and CRANNI
  const getTestLp = async () => {
    if (!address) {
      lbDialog.fire(t('token.notConnected'), t('tx.connectFirst'), "warning");
      return;
    }
    try {
      const mintHash = await writeContractAsync({
        address: lpToken,
        abi: TEST_MINT_ABI,
        functionName: "mint",
        args: [address, TEST_LP_AMOUNT],
      });
      const mintReceipt = await provider.waitForTransaction(mintHash);
      if (!mintReceipt.status) throw new Error("Mint transaction failed.");
      refetchLpBalance();
      lbDialog.fire(t('tx.success'), t('token.testLpSent'), "success");
    } catch (err) {
      console.error("Error minting test LP:", err);
      lbDialog.fire(t('tx.error'), t('testnet.failed'), "error");
    }
  };

  const handleDeposit = async () => {
    if (!amount) return lbDialog.fire(t('tx.error'), t('token.enterAmount'), "error");
    try {
      const parsedAmount = ethers.utils.parseUnits(amount, 18);

      if (parsedAmount.lt(lowerAmount)) {
        return lbDialog.fire(
          t('tx.error'),
          t('token.minDeposit', { amount: ethers.utils.formatUnits(lowerAmount, 18) }),
          "error"
        );
      }

      if (allowanceDeposit === 0n) {
        const approveHash = await writeContractAsync({
          address: lpToken,
          abi: IERC20ABI.abi,
          functionName: "approve",
          args: [stakingContract, ethers.constants.MaxUint256],
        });

        const approveReceipt = await provider.waitForTransaction(approveHash);
        if (!approveReceipt.status) throw new Error("Approval transaction failed.");

        lbDialog.fire({
          icon: "success",
          title: t('token.approvalSuccess'),
          text: t('token.lpApproved'),
          timer: 2000,
          timerProgressBar: true,
        });
      }

      const depositHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "deposit",
        args: [parsedAmount],
      });

      const depositReceipt = await provider.waitForTransaction(depositHash);
      if (!depositReceipt.status) throw new Error("Deposit transaction failed.");

      setAmount("0");
      setUserStakedAmount((prev) => (parseFloat(prev) + parseFloat(amount)).toFixed(18));
      setLpTokenBalance((prev) => (parseFloat(prev) - parseFloat(amount)).toFixed(18));
      lbDialog.fire(t('tx.success'), t('token.depositDone'), "success");
    } catch (err) {
      console.error("Error during deposit:", err);
      lbDialog.fire(t('tx.error'), t('token.txFailed'), "error");
    }
  };

  const claimRewards = async () => {
    try {
      lbDialog.fire({
        title: t('token.processing'),
        text: t('token.claiming'),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          lbDialog.showLoading();
        }
      });

      const claimHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "claimRewards",
      });

      const claimReceipt = await provider.waitForTransaction(claimHash);
      if (!claimReceipt.status) throw new Error("Claim transaction failed.");

      // The contract will handle partial payments automatically
      const claimedAmount = parseFloat(availableRewards);
      
      lbDialog.fire({
        icon: "success",
        title: t('token.claimed'),
        text: claimedAmount > 0
          ? t(userUnpaidRewards > 0 ? 'token.claimedAmountDebt' : 'token.claimedAmount', { amount: claimedAmount.toFixed(6) })
          : t('token.claimRecorded'),
        timer: 4000,
        timerProgressBar: true,
      });

    } catch (err) {
      console.error("Error during rewards withdrawal:", err);
      lbDialog.fire(t('tx.error'), t('token.txFailed'), "error");
    }
  };

  const handleWithdraw = async () => {
    if (!amount || !(parseFloat(amount) > 0)) return lbDialog.fire(t('tx.error'), t('token.enterAmount'), "error");

    try {
      const parsedAmount = ethers.utils.parseUnits(amount, 18);

      if (pendPenalization && pendPenalization !== "0" && amount > 0) {
        const result = await lbDialog.fire({
          title: t('token.penaltyTitle'),
          html: `${t('token.penaltyIntro', { time: pendPenalization })}<br>${t('token.penaltyBody')}<br><br>${t('token.penaltyConfirm')}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: t('token.withdrawAnyway'),
          cancelButtonText: t('common.cancel'),
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
        });

        if (!result.isConfirmed) return;
      }

      const withdrawHash = await writeContractAsync({
        address: stakingContract,
        abi: StakingABI.abi,
        functionName: "withdraw",
        args: [parsedAmount],
      });

      const withdrawReceipt = await provider.waitForTransaction(withdrawHash);
      if (!withdrawReceipt.status) throw new Error("Withdrawal transaction failed.");

      setAmount("0");
      setUserStakedAmount((prev) => (parseFloat(prev) - parseFloat(amount)).toFixed(18));
      setLpTokenBalance((prev) => (parseFloat(prev) + parseFloat(amount)).toFixed(18));
      lbDialog.fire(t('tx.success'), t('token.withdrawDone'), "success");
    } catch (err) {
      console.error("Error during withdrawal:", err);
      lbDialog.fire(t('tx.error'), t('token.txFailed'), "error");
    }
  };

  const SubMenu = () => (
    <div className="mb-8 flex w-full justify-center">
      <div className="lb-seg">
        {[
          { key: "token", label: t('token.info') },
          { key: "stakingDashboard", label: t('token.stakingDashboard') },
          { key: "cynths", label: t('nav.cynths') },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            data-active={activeSection === tab.key}
            className="lb-seg-item"
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  const CranniInfoSection = () => (
    <div className="lb-section p-6 sm:p-8">
      <div className="text-center">
        <p className="lb-eyebrow">{t('token.overview')}</p>
        <h3 className="lb-heading mt-3 text-3xl">
          <AccentTitle text={t('token.cranniTitle', { accent: ACCENT })} accent="CRANNI" />
        </h3>
        <p className="mt-3 text-sm text-white/60">
          {t('heroToken.subtitle')}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          { label: t('token.currentPrice'), value: `$${cranniPrice}` },
          { label: t('token.marketCap'), value: `$${cranniMarketCap.toLocaleString()}` },
          { label: t('token.totalSupply'), value: cranniTotalSupply.toLocaleString() },
        ].map((stat) => (
          <div key={stat.label} className="lb-tile lb-hoverable p-4 text-center">
            <p className="lb-label">{stat.label}</p>
            <p className="notranslate lb-num mt-2 font-display text-2xl text-white">{stat.value}</p>
          </div>
        ))}
      </div>

      {chainId === TESTNET_CHAIN ? (
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <button type="button" onClick={getTestLp} className="lb-btn lb-btn-primary">
            {t('token.getTestLp')}
          </button>
          <p className="max-w-md text-sm text-white/60">{t('token.testLpNote')}</p>
        </div>
      ) : (
        <div className="mt-8 flex justify-center">
          <a
            href={`https://quickswap.exchange/#/add/ETH/${cranniAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="lb-btn lb-btn-primary"
          >
            {t('token.addLiquidity')}
          </a>
        </div>
      )}
    </div>
  );

  const TokenInfoContent = () => (
    <div className="mt-8 space-y-6">
      <div className="lb-card p-6 sm:p-8">
        <p className="lb-eyebrow">{t('token.utility')}</p>
        <h3 className="lb-heading mt-3 text-2xl sm:text-3xl">
          <AccentTitle text={t('token.utilityTitle', { accent: ACCENT })} accent={t('token.utilityAccent')} />
        </h3>
        <p className="mt-3 max-w-2xl text-white/65">
          {t('token.utilityIntro')}
        </p>

        <div className="mt-7 grid gap-4 md:grid-cols-3">
          {[
            {
              title: t('token.syntheticCollateral'),
              body: t('token.collateralBody'),
              dot: 'bg-emerald-400',
            },
            {
              title: t('token.lpStaking'),
              body: t('token.lpBody'),
              dot: 'bg-sky-400',
            },
            {
              title: t('token.smartUtility'),
              body: t('token.smartBody'),
              dot: 'bg-amber-400',
            },
          ].map((item) => (
            <div key={item.title} className="lb-tile lb-hoverable p-5">
              <span className={`inline-block h-2 w-2 rounded-full ${item.dot}`} aria-hidden="true" />
              <h4 className="mt-3 text-lg font-bold text-white">{item.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{item.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="lb-section p-6 sm:p-8">
        <p className="lb-eyebrow">{t('token.walletNative')}</p>
        <h3 className="lb-heading mt-3 text-2xl sm:text-3xl">
          <AccentTitle text={t('token.mechanismTitle', { accent: ACCENT })} accent={t('token.mechanismAccent')} />
        </h3>
        <p className="mt-3 max-w-2xl text-white/70">
          {t('token.mechanismIntro')}
        </p>

        <div className="mt-7 grid gap-4 md:grid-cols-2">
          <div className="lb-tile p-5">
            <div className="flex items-center gap-2">
              <ArrowDownLeft className="h-4 w-4 text-emerald-400" aria-hidden="true" />
              <h4 className="text-lg font-bold text-white">{t('token.buying')}</h4>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-white/65">
              {t('token.buyBody')}
            </p>
          </div>

          <div className="lb-tile p-5">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-rose-400" aria-hidden="true" />
              <h4 className="text-lg font-bold text-white">{t('token.selling')}</h4>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/65">
              <li className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="notranslate lb-num font-bold text-white">0.01 POL</span>
                <span>{t('token.sellAll')}</span>
              </li>
              <li className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
                <span className="notranslate lb-num font-bold text-white">0.005 POL</span>
                <span>{t('token.sellHalf')}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="notranslate lb-num font-bold text-white">0.0025 POL</span>
                <span>{t('token.sellQuarter')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm italic text-white/55">
            {t('token.walletNativeBlurb')}
          </p>
          <button onClick={approveForAutoBuy} className="lb-btn lb-btn-primary shrink-0">
            {t('token.approveAuto')}
          </button>
        </div>
      </div>

      <p className="mx-auto max-w-3xl text-center text-sm text-white/50">
        {t('token.closing')}
      </p>
    </div>
  );

  const inputRef = React.useRef();

  const StakingDashboardContent = () => (
    <div className="flex flex-col gap-8">
      <div className="text-center">
        <p className="lb-eyebrow">{t('token.liquidityMining')}</p>
        <h1 className="lb-heading mt-3 text-3xl sm:text-4xl">
          <AccentTitle text={t('token.stakingTitle', { accent: ACCENT })} accent={t('token.stakingAccent')} />
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-white/55 sm:text-base">
          {t('token.stakingBlurb')}
        </p>
      </div>
      
      <DebtWarning userUnpaidRewards={parseFloat(userUnpaidRewards)} />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="APR"
          value={`${apr}%`}
          icon={<TrendingUp color="#10b981" size={24} />}
          className="border-emerald-400/25 bg-emerald-400/[0.06]"
        />
        <StatCard
          title={t('token.yourStaked')}
          value={`${parseFloat(userStakedAmount).toFixed(8)} CRANNI-POL ($${parseFloat(
            userStakedAmount * lpPrice
          ).toFixed(2)})`}
          icon={<Coins color="#3b82f6" size={24} />}
        />
       <StatCard
          title={t('token.pendingRewards')}
          value={
            <div className="flex flex-col gap-1">
              <div className="text-lg font-bold text-white">
                {pendingRewards} USDT
              </div>
              {userUnpaidRewards > 0 && (
                <div className="flex flex-col text-xs">
                  <div className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-emerald-300">Available: {availableRewards} USDT</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <span className="text-amber-400">Pending: {userUnpaidRewards} USDT</span>
                  </div>
                </div>
              )}
            </div>
          }
          icon={<Gift color="#9a65cb" size={24} />}
          onClaim={claimRewards}
          className="border-emerald-400/25 bg-emerald-400/[0.06]"
          address={address}
          rewards={pendingRewards}
        />
        <StatCard
          title={t('token.lpBalance')}
          value={`${parseFloat(lpTokenBalance).toFixed(8)} CRANNI-POL ($${parseFloat(
            lpTokenBalance * lpPrice
          ).toFixed(2)})`}
          icon={<Droplet color="#ec4899" size={24} />}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="lb-card p-6">
          <div className="lb-seg mb-6 flex w-full">
            <button
              onClick={() => setIsDepositMode(true)}
              data-active={isDepositMode}
              className="lb-seg-item flex-1"
            >
              {t('token.deposit')}
            </button>
            <button
              onClick={() => setIsDepositMode(false)}
              data-active={!isDepositMode}
              className="lb-seg-item flex-1"
            >
              {t('token.withdraw')}
            </button>
          </div>
          <div className="space-y-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                placeholder={`Amount to ${isDepositMode ? "Deposit" : "Withdraw"}`}
                value={amount}
                autoFocus={inputRef.current === document.activeElement}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  if (value !== amount) {
                    setAmount(value);
                    inputRef.current?.focus();
                  }
                }}
                className="lb-input p-4 pr-20"
              />
              <button
                onClick={setMaxAmount}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white/80 transition hover:bg-white/20 hover:text-white"
              >
                MAX
              </button>
            </div>
            <button
              onClick={isDepositMode ? handleDeposit : handleWithdraw}
              className={`lb-btn lb-btn-lg w-full ${isDepositMode ? "lb-btn-primary" : "lb-btn-danger"}`}
            >
              {isDepositMode ? "Deposit" : "Withdraw"}
            </button>
          </div>
        </div>
        <div className="lb-card p-6">
          <h3 className="lb-heading text-xl mb-5 lb-gradient-text">
            {t('token.statistics')}
          </h3>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.tvl')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-white">
                {parseFloat(totalStaked).toFixed(3)} LP (${parseFloat(totalStaked * lpPrice).toFixed(2)})
              </span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.contractReward')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-white">{totalRewardBalance} USDT</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.availableReward')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-white">{rewardBalance} USDT</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.totalDebt')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-rose-400">{totalUnpaidRewards} USDT</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.yourUnpaid')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-rose-400">{userUnpaidRewards} USDT</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.rewardPerBlock')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-white">{rewardPerBlock} USDT</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
              <span className="text-sm text-white/55">{t('token.penaltyTime')}</span>
              <span className="notranslate lb-num shrink-0 text-right text-sm font-bold text-white">{pendPenalization}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="lb-page relative min-h-screen w-full px-3 py-8 sm:px-6 sm:py-10">
      <div className="relative mx-auto w-full max-w-screen-xl">
        <SubMenu />
        {activeSection === "token" && <CranniInfoSection />}
        {activeSection === "token" && <TokenInfoContent />}
        {activeSection === "stakingDashboard" && <StakingDashboardContent />}
        {activeSection === "cynths" && <MintBurn />}
      </div>
    </div>
  );
};

export default Token;